import Cookies from 'js-cookie';
import { values } from 'mobx';
import { omit, mapValues, isPlainObject, isEqual, get } from 'lodash-es';
import { flow, getEnv, types, getParent } from 'mobx-state-tree';
import { formatInTimeZone } from 'date-fns-tz';
import { subDays } from 'date-fns';
import { stateToTzMap } from '@ourbranch/state-to-tz-map';
import { isProductionEnv } from '@ourbranch/env-helpers';
import { policyDetailsFromSegments } from '@ourbranch/policy-utils';
import {
  bixStates,
  advancedConnectedHomeStates,
  discountInventoryScoreStates,
  discountPaperlessStates,
  leaseLoanStates,
  policyType,
  lapsedLicenseMonthsStates,
  rejectionTypeToPolicyTypeMap,
  offerMergingNotAllowedAffinityCodes
} from '@ourbranch/lookups';
import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';

import awsExports from 'aws-exports';
import { formattedError, hasRejection, mapRevisedQuoteDetails } from 'core/helpers/quoter.service';
import { appConfig, brandConfig } from 'brand-config';
import {
  getRateControlDateConfig,
  getBindThresholdDays,
  isRateControlDateOlderThan,
  getStalePaymentScheduleDate
} from 'offer/helpers/rate-control-date';
import {
  ABODE_PRO_PLAN_AFFINITY,
  ABODE_PRO_PLAN_PROVIDER_NAME,
  NEW_CUSTOMER_HOME_SECURITY
} from 'core/helpers/constants';
import Notification from 'core/helpers/notifications';
import { isComparativeRaterQuote } from 'core/helpers/offer-redirect';
import { phoneNumberFormatter } from 'core/helpers/formatters';
import { isValidPhoneNumber } from 'libphonenumber-js/max';
import { withRetry } from 'core/helpers/with-retry';
import { determineIfOfferNeedsUWForm, getUWNotification } from 'core/helpers/underwriting-form-helpers';
import { emptyConnectedHome } from 'common/helpers/connected-home';
import { applyCosmeticDamageRulesToOffer } from 'common/helpers/cosmetic-damage-exclusion-helpers';
import getQuoteFriendlySelectedOption from 'offer/helpers/quote-friendly-selected-option';
import { usesDocGenQuotePdf } from 'offer/helpers/quote-pdf-pipeline';
import {
  uploadOfferDocument,
  fetchOfferDocuments,
  downloadOfferDocument,
  getOfferDocumentSignedUrl,
  setOfferDocumentLabel,
  deleteOfferDocument
} from 'offer/helpers/offer-document-service';
import {
  GET_OFFER,
  GET_OFFER_VENDOR_DATA,
  RECALCULATE_QUOTE_TO_CLUSTER,
  UW_APPROVE_FOR_CHECKOUT,
  REQUEST_BIND,
  DOWNLOAD_QUOTE,
  ADD_DRIVERS_CARS_AND_RECALCULATE,
  DOWNLOAD_OFFER,
  GET_CAR_DRIVER_SUGGESTIONS,
  ADD_TO_PRE_BIND_UW_REJECTIONS,
  GET_PRE_BIND_UW_REJECTIONS_BY_WEB_USER_ID,
  CLEAR_REJECT_CODES,
  ADD_REFERRAL,
  SWAP_HOME_RENTERS,
  FILL_OUT_SKIPPED_DATA_PULLS,
  SEND_PURCHASE_LINK,
  SEND_OFFER_LINK_TO_REP,
  GET_EXISTING_CUSTOMER_POLICIES,
  UPDATE_OFFER_ADVANCED_CONFIGURATION,
  GET_MERGED_OFFER,
  GET_OFFER_DIFFERENCE,
  SEND_OFFER_FOR_UNDERWRITING_APPROVAL
} from './offer-queries';
import {
  fetchBoundPolicy,
  findNewerStaffEditedOffer,
  getInitialSelectedOption,
  getLinkedPolicyUmbrellaInputs,
  hasSoftRejectionForOption,
  pollPolicyTable,
  shouldApplyBoundPolicyResult
} from './offer-functions';
import { calculateMarketTier } from 'core/helpers/misc';
import { getMVRCheckStatus } from 'core/helpers/mvr-helpers';
import { track, identify } from 'core/helpers/analytics';
import { MatchingActivePolicyErrorCode, parseMatchingActivePolicyIds } from './matching-active-policy';

import PolicyList from '../account-store/policies-store/models/policy-list';
import { initialState } from '../store.mobx';

// Mutations get a single retry: re-issuing them looks safe (recalcs build a fresh cluster per
// run, UW-rejection writes are keyed by webUserId) but hasn't been formally audited for
// idempotency, so bound the duplicate work instead of using withRetry's default 5 attempts.
const MUTATION_MAX_ATTEMPTS = 2;

export const OfferPageTabs = {
  SETTINGS: 'SETTINGS',
  AUTO: 'AUTO',
  HOME: 'HOME',
  PEOPLE: 'PEOPLE',
  PIP: 'PIP',
  RENTERS: 'RENTERS',
  CONDO: 'CONDO',
  FARM: 'FARM'
};

export const OfferStatus = {
  Initial: 'Initial',
  Purchasing: 'Purchasing',
  PurchaseFailed: 'PurchaseFailed',
  DownloadingQuote: 'DownloadingQuote',
  DownloadingOffer: 'DownloadingOffer',
  DownloadingSimpleQuote: 'DownloadingSimpleQuote',
  FetchingPolicyFromOffer: 'FetchingPolicyFromOffer',
  FetchSuccessful: 'FetchSuccessful',
  FetchFailed: 'FetchFailed',
  MVRRetry: 'MVRRetry',
  MVRRejected: 'MVRRejected',
  MVRDriversChanged: 'MVRDriversChanged',
  PreBindRejectionsBlocking: 'PreBindRejectionsBlocking',
  DuplicatedBindError: 'DuplicatedBindError',
  // #35249: a matching active policy (same insured + primary property) warns instead of hard-blocking.
  // The agent confirms in a dialog and we resubmit the bind with ignoreMatchingActivePolicyForBind.
  MatchingActivePolicyWarning: 'MatchingActivePolicyWarning'
};

export const MVRErrors = {
  MVR_RETRY: 'MVRRetry',
  MVR_REJECTED: 'MVRRejected',
  MVR_DRIVERS_CHANGED: 'MVRDriversChanged'
};

export const PreBindErrors = {
  REJECTIONS_BLOCKING: 'PreBindRejectionsBlocking'
};

export const DuplicatedBindError = 'DUPLICATE_REQUEST_TO_BIND';

// #35249: marker the pre-bind step appends (ERROR_CODE: MATCHING_ACTIVE_POLICY) when a bind matches
// an active policy for the same insured + primary property. Non-numeric, so it never looks like a
// billing decline; staff-fe keys the "purchase anyway?" confirmation on it.
// Re-exported (imported above) from a store-free module so they can be unit-tested in isolation (#35249).
export { MatchingActivePolicyErrorCode, parseMatchingActivePolicyIds };

export const FormAction = {
  Update: 'Update',
  Checkout: 'Checkout',
  UpdateEffectiveDates: 'UpdateEffectiveDates',
  PreBindUWValidation: 'PreBindUWValidation',
  FillSkippedDataPullsAll: 'FillSkippedDataPullsAll',
  FillSkippedDataPullsNonMVR: 'FillSkippedDataPullsNonMVR'
};

const FETCH_POLICY_ATTEMPTS = 30;

/**
 * Re-applies the cosmetic damage rules to a server response so the form's initial values agree with
 * what the risk forms would set (prevents spurious dirty state). The rules live in
 * common/helpers/cosmetic-damage-exclusion-helpers (#35870: an underwriter-overridden risk is kept
 * "Included" rather than re-excluded).
 */
const applyCosmeticDamageBusinessLogic = (offer) => applyCosmeticDamageRulesToOffer(offer, appConfig);

const Error = types.model({
  type: types.maybeNull(types.string),
  code: types.union(types.string, types.number),
  message: types.maybeNull(types.string),
  data: types.maybeNull(types.frozen()),
  // ISO timestamp captured when the error was raised, surfaced as "Time of error"
  // in the offer-flow system error modal (GitHub #30224).
  timestamp: types.maybeNull(types.string)
});

const Driver = types
  .model({
    id: types.identifier,
    isPrimary: types.boolean,
    isCoApplicant: types.boolean,
    isSwappable: types.boolean,
    hasUDR: types.boolean,
    willRecheckDrivingRecord: false,
    excludeDriverDisabled: types.maybeNull(types.boolean)
  })
  .actions((self) => ({
    toggleCoApplicantStatus() {
      self.isCoApplicant = !self.isCoApplicant;
    },
    toggleWillRecheckDrivingRecord() {
      self.willRecheckDrivingRecord = !self.willRecheckDrivingRecord;
    }
  }));

const OfferDocument = types.model({
  id: types.identifier,
  fileName: types.string,
  uploadedAt: types.string,
  displayName: types.maybeNull(types.string)
});

const cleanInput = (input) => {
  return mapValues(omit(input, ['__typename']), (val) => {
    if (isPlainObject(val)) {
      return cleanInput(omit(val, ['__typename']));
    }
    return val;
  });
};

const mapBindRequestToInput = (bindRequest) => {
  return cleanInput(bindRequest);
};

const OfferStore = types
  .model({
    currentTab: types.optional(types.enumeration('OfferPageTabs', Object.values(OfferPageTabs)), OfferPageTabs.PEOPLE),
    errors: types.array(Error),
    loading: types.boolean,
    recalculatingPrice: types.optional(types.boolean, false),
    addingReferral: types.boolean,
    offer: types.maybeNull(types.frozen()),
    openDialog: types.boolean,
    drivers: types.map(Driver),
    mortgageOptions: types.maybeNull(types.frozen()),
    selectedOption: types.maybeNull(types.string),
    status: types.string,
    purchasedAccountId: types.maybeNull(types.string),
    formAction: types.string,
    includeConnectedHome: types.boolean,
    includeInventory: types.boolean,
    includePaperless: types.boolean,
    includeBixMutualAffinity: types.boolean,
    includeMultiPolicyDiscount: types.boolean,
    // #30919: which carrier the bundled auto policy is with ('BIX' | 'Other'), meaningful
    // only when includeMultiPolicyDiscount is true. Drives the rater's 3-level auto_homeYN.
    autoBundleCarrier: types.maybeNull(types.string),
    includeLegacyDiscount: types.boolean,
    includeMilitaryDiscount: types.boolean,
    includeEducationDiscount: types.boolean,
    includeCropInsuranceDiscount: types.boolean,
    includeTraditionsDiscount: types.boolean,
    includeAssociationDiscount: types.boolean,
    includeClaimFreeDiscount: types.maybe(types.boolean),
    notifications: types.maybeNull(types.array(types.string)),
    vinDiscrepancies: types.maybeNull(types.array(types.frozen())),
    carAndDriverSuggestions: types.maybeNull(types.frozen()),
    checkoutFormData: types.maybeNull(types.frozen()),
    showConectedHomeModal: types.boolean,
    showFarmAssociationModal: types.boolean,
    showUDRModal: types.boolean,
    needsPreBindUWForm: types.boolean,
    showRaterInterstitialModal: types.boolean,
    priorQuoteWithPreBindUWRejections: types.model({
      ineligibleForHome: false,
      ineligibleForAuto: false
    }),
    areRejectCodesCleared: types.boolean,
    showBranchPlusMultiPolicyDiscountModal: types.boolean,
    includeBranchPlusMultiPolicyDiscount: types.boolean,
    failedToSendLink: types.maybeNull(types.boolean, false),
    scrollTo: types.maybeNull(types.string),
    existingCustomerPolicies: types.array(PolicyList),
    marketTierRating: types.maybeNull(types.number),
    uiHelpers: types.model({
      eligibleStudentDriversCardsOpen: types.boolean,
      eligibleDrivingCourseDriversCardsOpen: types.boolean,
      eligibleCommunityDriveDriversCardsOpen: types.boolean
    }),
    offerDifference: types.maybeNull(types.frozen()),
    documents: types.array(OfferDocument),
    documentCount: types.optional(types.number, 0),
    documentsLoading: types.optional(types.boolean, false),
    rawBindErrorMessages: types.maybeNull(types.string),
    redirectTarget: types.maybeNull(types.frozen()),
    isNavigatingInternally: types.optional(types.boolean, false),
    vendorData: types.maybeNull(types.frozen()),
    // Policy already associated with this offer ({ id, accountId, offerId }), if any — drives the
    // "already purchased" warning banner on the offer page (GitHub issue #31622)
    boundPolicy: types.maybeNull(types.frozen()),
    // #35936: true when boundPolicy was bound from THIS exact offer id (hard block on Send-to-UW);
    // false when it's a fuzzy member+location match from a different/unknown quote (soft warning).
    boundPolicyIsExact: types.optional(types.boolean, false),
    // Offer id of the most recent getOffer. A slow load or bound-policy lookup for an offer
    // opened earlier must not stamp its result onto the one opened after it (#31622).
    requestedOfferId: types.maybeNull(types.string),
    // #35249: active policy ids that match this offer's insured + primary property, surfaced in the
    // "purchase anyway?" confirmation when a staff bind hits the (non-blocking) duplicate warning.
    matchingActivePolicyIds: types.optional(types.array(types.string), []),
    // The bind input of the attempt that triggered the matching-active-policy warning, so the
    // confirmation can resubmit it with ignoreMatchingActivePolicyForBind set.
    pendingBindRequestInput: types.maybeNull(types.frozen())
  })
  // Transient (not persisted) underwriter opt-in to hold the Rate Control Date for this
  // session/bind (GitHub issue #32130). Set from the Billing tab checkbox, read when
  // assembling the bind request and when computing isStale. Intentionally volatile: it is a
  // bind-time choice, not part of the offer snapshot, and must not feed recalculate.
  // Stored as the id of the offer it was checked on, not a boolean: this store outlives the
  // offer it shows, and a bare flag carried over to the next offer, which then bound at its
  // old rate with no one choosing to hold it. See the holdRateControlDate view.
  .volatile(() => ({
    holdRateControlDateOfferId: null,
    // #35629: set by the footer when "continue to checkout" first has to refresh a stale prebind
    // payment schedule; recalculateQuoteToCluster then lands on checkout instead of the offer page.
    continueToCheckoutAfterRecalculate: false,
    // #35629: the silent refresh did not produce a current schedule (re-rate error, rejection, or
    // still stale) — the footer falls back to an explicit Update prompt.
    paymentScheduleRefreshFailed: false
  }))
  .views((self) => ({
    get userPreferences() {
      return getParent(self).userPreferences;
    },
    get canMergeOffer() {
      return (
        !offerMergingNotAllowedAffinityCodes.includes(self.offer?.quote?.global?.affinity) &&
        isComparativeRaterQuote(self.offer)
      );
    },
    get shouldShowAutoTab() {
      return self?.selectedOption?.includes(policyType.Auto);
    },
    get linkedPolicyUmbrellaInputs() {
      return getLinkedPolicyUmbrellaInputs(self.existingCustomerPolicies);
    },
    get shouldShowRentersTab() {
      return self?.selectedOption?.includes(policyType.Renters);
    },
    get shouldShowCondoTab() {
      return self?.selectedOption?.includes(policyType.Condo);
    },
    get shouldShowHomeTab() {
      return self?.selectedOption?.includes(policyType.Home);
    },
    get shouldShowFarmTab() {
      return isFarmBasedPolicyType(self?.selectedOption);
    },
    get shouldShowPIPTab() {
      return self?.selectedOption?.includes(policyType.Auto) && self.state === 'MI';
    },
    get shouldShowAdditionalPaymentsSection() {
      /* right now, the only additional price that can be added is for Abode Pro Plan, noted by the abode affinity code */
      const affinity = self.offer?.quote?.global?.affinity;
      const connectedHomeEnabled = self.offer?.quote?.connectedHome && self.offer?.quote?.connectedHome?.providerName;
      const providerIsAbodeProPlan = self.offer?.quote?.connectedHome?.providerName === ABODE_PRO_PLAN_PROVIDER_NAME;
      return affinity === ABODE_PRO_PLAN_AFFINITY && connectedHomeEnabled && providerIsAbodeProPlan;
    },
    get state() {
      return isFarmBasedPolicyType(self?.selectedOption)
        ? self.offer?.quote.farmLocations[0].address.state
        : self.offer?.quote.correctedAddress.state;
    },
    get timezone() {
      return stateToTzMap[self.state];
    },
    get isBixOfferOrState() {
      return self.offer.quote.isBix || bixStates[self.state];
    },
    get isBix() {
      return self.offer?.quote?.isBix;
    },
    get showLeaseLoan() {
      return self.isBix ? leaseLoanStates[self.state]?.isBix : leaseLoanStates[self.state]?.isNotBix;
    },
    get isAdvancedConnectedHome() {
      return self.isBix
        ? advancedConnectedHomeStates[self.state]?.isBix
        : advancedConnectedHomeStates[self.state]?.isNotBix;
    },
    get allowInventoryDiscount() {
      return self.isBix
        ? discountInventoryScoreStates[self.state]?.isBix
        : discountInventoryScoreStates[self.state]?.isNotBix;
    },
    get allowPaperlessDiscount() {
      return self.isBix ? discountPaperlessStates[self.state]?.isBix : discountPaperlessStates[self.state]?.isNotBix;
    },
    get coApplicant() {
      return values(self.drivers).find((driver) => driver.isCoApplicant);
    },
    get excludeDriverDisabled() {
      return values(self.drivers).find((driver) => driver.excludeDriverDisabled);
    },
    get isFarmOffer() {
      return isFarmBasedPolicyType(self.selectedOption);
    },
    get isBranchPlusOnly() {
      return self.offer && self.offer.options?.length === 0 && self.offer.branchPlusOffers?.length > 0;
    },
    isCoApplicantButtonDisabled(driverID, formValues) {
      /*
      if the driver is in the form values but not in the store,
      then the offer hasn't been updated to add the driver yet.
      updating needs to happen before swap is enabled
      */
      const driverInStore = self.offer?.quote.drivers.find((driver) => driver.id === driverID);
      const driverInFormValues = formValues?.drivers.find((driver) => driver.id === driverID);
      if (driverInFormValues && !driverInStore) {
        return true;
      }
      return false;
    },
    get defaultSelectedOption() {
      return self.offer?.options.find(({ name }) => name === 'Bundle')?.type || self.offer?.options[0]?.type;
    },
    // Rate Control Date hold window for this brand (GitHub issue #32130), defaulting to
    // {0,0} (Branch / unconfigured = legacy "must be today").
    get rateControlDateConfig() {
      return getRateControlDateConfig(brandConfig);
    },
    // Days the RCD may lag today for this offer right now: the underwriter Y window when the
    // "Hold Rate Control Date" checkbox is set, otherwise the default X window.
    get bindRateControlDateThresholdDays() {
      return getBindThresholdDays(self.rateControlDateConfig, self.holdRateControlDate);
    },
    // True only while the offer the underwriter held is the one on screen.
    get holdRateControlDate() {
      return !!self.offer?.id && self.holdRateControlDateOfferId === self.offer.id;
    },
    get isStale() {
      // "Stale" means the rateControlDate (RCD) has fallen outside the brand's bind window
      // — i.e. it is more than `bindRateControlDateThresholdDays` days old. This drives the
      // footer's update-nag and bind gating. For Branch the threshold is 0, so this reduces
      // to "RCD !== today" (the original behavior). For farm brands an RCD within X days
      // (or Y when an underwriter holds it) is NOT stale, so the offer binds without a
      // re-rate. See offer/helpers/rate-control-date and issue #32130.
      //
      // RCD is a 'YYYY-MM-DD' string compared in the offer state's timezone. The helper is
      // lenient (not stale) when RCD or the tz is unknown — an unrecognised territory would
      // otherwise fall back to the browser tz and shift the date a day around midnight
      // (#30635). The backend bind gate is the authoritative check.
      const rateControlDate = self.offer?.quote?.global?.rateControlDate;
      if (!rateControlDate) {
        return false;
      }
      return isRateControlDateOlderThan({
        rateControlDate,
        state: self.state,
        maxDaysOld: self.bindRateControlDateThresholdDays
      });
    },
    // #35629: the day the selected option's prebind payment schedule was computed on, when that is no
    // longer today on a mutual brand — else null. The schedule is computed at rating time but autobill
    // schedules from the bind day, so checkout requires an Update (price-neutral within the brand's
    // rate control date window) to refresh it. See offer/helpers/rate-control-date.
    get stalePaymentScheduleDate() {
      const selectedOption = getQuoteFriendlySelectedOption(self.selectedOption);
      return getStalePaymentScheduleDate({
        option: self.offer?.options?.find((o) => o.type === selectedOption),
        global: self.offer?.quote?.global,
        rateControlDate: self.offer?.quote?.global?.rateControlDate,
        state: self.state,
        config: self.rateControlDateConfig
      });
    },
    get isOutdated() {
      // YYYY-MM-DD strings sort lexicographically the same as chronologically,
      // so a string compare against today-minus-30-days in the offer state's
      // timezone gives the right answer without the timezone-math hazards of
      // the previous implementation. (#30635)
      const rateControlDate = self.offer?.quote?.global?.rateControlDate;
      const tz = self.state ? stateToTzMap[self.state] : null;
      if (!rateControlDate || !tz) {
        return false;
      }
      const sixtyDaysAgoStr = formatInTimeZone(subDays(new Date(), 60), tz, 'yyyy-MM-dd');
      return rateControlDate < sixtyDaysAgoStr;
    },
    get triggerDrivingRecordRecheck() {
      return values(self.drivers).some((driver) => driver.willRecheckDrivingRecord);
    },
    get driverIdsToRecheck() {
      return values(self.drivers)
        .filter((driver) => driver.willRecheckDrivingRecord)
        .map((driver) => driver.id);
    },
    get hasOneOrMoreUDRs() {
      return values(self.drivers).some((driver) => driver.hasUDR);
    },
    get softRejectionOnSelectedOption() {
      return hasSoftRejectionForOption(self.offer?.rejections, self.selectedOption);
    },
    get hardRejectionOnOffer() {
      return self.offer?.rejections?.some((rejection) => rejection.level === 'hard');
    },
    get primaryNamedInsured() {
      const primaryDriver = self.offer?.quote?.drivers?.find((driver) => driver.isPrimary);
      if (primaryDriver?.firstName && primaryDriver?.lastName) {
        return `${primaryDriver.firstName} ${primaryDriver.lastName}`;
      }
      return '';
    },
    get hasBundleOption() {
      if (self.offer?.options) {
        return self.offer.options.some((option) => option.name === 'Bundle');
      }
      return false;
    },
    get isApiOffer() {
      return self.offer?.quote?.global?.fromApi;
    },
    get hasDocuments() {
      return self.documents.length > 0;
    },
    get documentCountDisplay() {
      return `${self.documentCount} document${self.documentCount !== 1 ? 's' : ''}`;
    },
    get webUserId() {
      return self.offer?.quote?.global?.webUserId;
    }
  }))
  .actions((self) => ({
    // Underwriter opt-in to hold the Rate Control Date for this bind (issue #32130). Toggled
    // from the Billing tab checkbox; read when assembling the bind request and by isStale.
    setHoldRateControlDate(value) {
      self.holdRateControlDateOfferId = value ? (self.offer?.id ?? null) : null;
    },
    setFormAction({ dirty, stale, outDated, fromHomeVerification = false, action }) {
      const isExpiredOffer = stale && outDated; // more than 30 days
      const isOutOfDateOffer = stale && !outDated;
      if (action) {
        // between 1-29 days old
        self.formAction = action;
        return;
      }
      if (!dirty && !stale) {
        const { hasOnlyMVRToRun, hasAtLeastMVRToPull } = getMVRCheckStatus(self, dirty, self.offer?.quote);
        if (hasAtLeastMVRToPull) {
          self.formAction = FormAction.FillSkippedDataPullsNonMVR;
          return;
        }
        if (hasOnlyMVRToRun) {
          self.formAction = FormAction.FillSkippedDataPullsAll;
          return;
        }
      }

      if (fromHomeVerification) {
        self.formAction = FormAction.PreBindUWValidation;
        return;
      }
      if (isExpiredOffer) {
        self.formAction = FormAction.UpdateEffectiveDates;
      } else if (dirty || isOutOfDateOffer) {
        self.formAction = FormAction.Update;
      } else {
        self.formAction = FormAction.Checkout;
      }
    },
    setContinueToCheckoutAfterRecalculate(continueToCheckout) {
      self.continueToCheckoutAfterRecalculate = continueToCheckout;
    },
    setPaymentScheduleRefreshFailed(failed) {
      self.paymentScheduleRefreshFailed = failed;
    },
    // Both #35629 refresh flags are per-attempt: clear them when an offer loads and on an explicit
    // Update, so one failed refresh can't disable the silent refresh on the next stale offer.
    resetPaymentScheduleRefresh() {
      self.continueToCheckoutAfterRecalculate = false;
      self.paymentScheduleRefreshFailed = false;
    },
    setIncludeConnectedHome(include) {
      self.includeConnectedHome = include;
    },
    setIncludeInventory(include) {
      self.includeInventory = !!include;
    },
    setIncludePaperless(include) {
      self.includePaperless = !!include;
    },
    setIncludeBixMutualAffinity(include) {
      self.includeBixMutualAffinity = !!include;
    },
    setIncludeMultiPolicyDiscount(include) {
      self.includeMultiPolicyDiscount = !!include;
    },
    setAutoBundleCarrier(carrier) {
      self.autoBundleCarrier = carrier ?? null;
    },
    setIncludeLegacyDiscount(include) {
      self.includeLegacyDiscount = !!include;
    },
    setIncludeMilitaryDiscount(include) {
      self.includeMilitaryDiscount = !!include;
    },
    setIncludeEducationDiscount(include) {
      self.includeEducationDiscount = !!include;
    },
    setIncludeCropInsuranceDiscount(include) {
      self.includeCropInsuranceDiscount = !!include;
    },
    setIncludeTraditionsDiscount(include) {
      self.includeTraditionsDiscount = !!include;
    },
    setIncludeAssociationDiscount(include) {
      self.includeAssociationDiscount = !!include;
    },
    setIncludeClaimFreeDiscount(include) {
      self.includeClaimFreeDiscount = include;
    },
    setDrivers(drivers) {
      self.drivers.clear();
      drivers.forEach((driver) => {
        const { id, isPrimary, isCoApplicant, excludeDriver: excludeDriverDisabled } = driver;
        const isSwappable = isPrimary || isCoApplicant;
        const hasUDR = driver.autoViolations?.UDR >= 1;
        self.drivers.set(id, { id, isPrimary, isCoApplicant, isSwappable, hasUDR, excludeDriverDisabled });
      });
    },
    getOffer: flow(function* getOffer(offerId) {
      const { client } = getEnv(self);
      self.toggleRaterInterstitialModal(false);
      self.loading = true;
      self.redirectTarget = null; // Clear any previous auto-redirect state
      self.resetPaymentScheduleRefresh();
      self.vendorData = null;
      // Best-effort, non-blocking: warn if this offer was already committed to a policy (#31622)
      self.boundPolicy = null;
      self.boundPolicyIsExact = false;
      self.requestedOfferId = offerId;
      fetchBoundPolicy(client, offerId).then((boundPolicy) => self.setBoundPolicy(boundPolicy, offerId));
      // Another offer was opened while this one loaded: drop this load's results
      const isSuperseded = () => self.requestedOfferId !== offerId;
      try {
        const response = yield client.query({
          query: GET_OFFER,
          variables: { offerId },
          fetchPolicy: 'no-cache'
        });
        if (isSuperseded()) {
          return;
        }

        if (response.data) {
          self.errors = [];
          const { offer } = response.data;

          // The store outlives the offer it shows, so clear what belongs to the previous one before
          // this one lands. Documents, suggestions and the reject-codes-cleared flag belong to the
          // offer. UW state and saved checkout data belong to the customer, and a re-rate of the
          // same quote (new offer id, same webUserId) still needs them: rejectOffer below records
          // the UW form's rejection.
          const isSameOffer = offer?.id === self.offer?.id;
          if (!isSameOffer) {
            self.documents.clear();
            self.documentCount = 0;
            self.carAndDriverSuggestions = null;
            self.areRejectCodesCleared = false;
          }
          const isSameCustomer = isSameOffer || (!!offer?.webUserId && offer.webUserId === self.offer?.webUserId);
          if (!isSameCustomer) {
            self.formAction = FormAction.Update;
            self.priorQuoteWithPreBindUWRejections = { ineligibleForHome: false, ineligibleForAuto: false };
            self.checkoutFormData = null;
          }

          // first try to sync with the URL selected option.
          // some refactor is needed here, because the rater is not updating the selected option with staff
          // since is not being added to the recalculate call.
          // Right now we have 3 sources of true that aren't synched, the store, the quote and the url
          const { pathname, search } = window.location;
          self.setSelectedOption(
            getInitialSelectedOption(offer, {
              // offer/<offerId>/<option>/checkout
              checkoutOption: pathname.split('/')[3],
              // /offer/<offerId>?rater=ez&type=HA — the option the comparative rater quoted
              raterOption: new URLSearchParams(search).get('type')
            })
          );
          if (hasRejection(offer)) {
            self.rejectOffer(offer);
          } else {
            self.offer = applyCosmeticDamageBusinessLogic(offer);
            self.errors = [];
          }

          self.setFormAction({
            dirty: false,
            stale: self.isStale,
            fromHomeVerification: false
          });

          // Set from the offer before the awaits below: a failed member-policies fetch fails the
          // load after this point and must not leave the previous offer's values. The UW form
          // decision is redone when the UW flags arrive; this covers that lookup failing too.
          self.includeConnectedHome = !emptyConnectedHome(offer?.quote?.connectedHome);
          self.setIncludeBranchPlusMultiPolicyDiscount(!!offer?.quote?.branchPlusPolicies?.length);
          self.includeInventory = !!offer?.quote?.global?.discountInventoryScore;
          self.includePaperless = !!offer?.quote?.global?.discountPaperless;
          self.includeBixMutualAffinity = !!offer?.quote?.global?.discountBixMutualAffinity;
          // Farm discounts
          self.includeMultiPolicyDiscount = !!offer?.quote?.global?.multiPolicyDiscount;
          self.autoBundleCarrier = offer?.quote?.global?.autoBundleCarrier ?? null;
          self.includeLegacyDiscount = !!offer?.quote?.global?.legacyDiscount;
          self.includeMilitaryDiscount = !!offer?.quote?.global?.militaryDiscount;
          self.includeEducationDiscount = !!offer?.quote?.global?.educationDiscount;
          self.includeCropInsuranceDiscount = !!offer?.quote?.global?.cropInsuranceDiscount;
          self.includeTraditionsDiscount = !!offer?.quote?.global?.traditionsDiscount;
          self.includeAssociationDiscount = !!offer?.quote?.global?.farmAssociationDiscount;
          self.includeClaimFreeDiscount = offer?.quote?.global?.claimFreeDiscount ?? undefined;
          self.setDrivers(offer.quote.drivers);
          self.setNeedsPreBindUWForm();

          self.fetchAffinityData();
          // Documents only feed the supporting-documents button/modal, which render off documentsLoading
          self.fetchDocuments().catch((error) => console.log('Error fetching documents:', error));

          // These read self.offer, so they run only after it is set. They are independent of each
          // other but the sync setters below consume their results: existing customer policies feed
          // setOfferMarketTier (home bundle / multiline market tier), pre-bind UW rejections feed
          // setNeedsPreBindUWForm and setNotifications. The newer-offer check must resolve before
          // the merge-modal decision — the auto-redirect wins over comparative-rater bridging
          // (#30994); internal navigation skips it because the interstitial sets the flag so the
          // destination doesn't immediately re-check.
          // Member policies are refetched every load: the previous offer's may be another member's.
          self.existingCustomerPolicies.clear();
          const [existingCustomerPoliciesResult] = yield Promise.allSettled([
            offer.quote.existingCustomer ? self.fetchExistingCustomerPolicies(offer.quote.existingCustomer.id) : null,
            self.checkForPriorQuoteWithPreBindUWRejections(),
            offer.webUserId && !self.isNavigatingInternally ? self.checkForNewerStaffEditedOffer(offer) : null
          ]);
          if (isSuperseded()) {
            return;
          }
          // Only the member-policies fetch can reject (the other two catch internally). Wait for all
          // three so nothing lands after the error path, then fail the load without a redirect, as
          // the serial chain did.
          if (existingCustomerPoliciesResult.status === 'rejected') {
            self.redirectTarget = null;
            throw existingCustomerPoliciesResult.reason;
          }
          self.getDriverAndCarSuggestions(self.offer.id);
          self.setNeedsPreBindUWForm();
          try {
            self.setOfferMarketTier();
          } catch (e) {
            console.log('Error setting market tier', e);
          }
          self.setNotifications();

          // Track offer opened event for Appcues state-based targeting
          try {
            const parent = getParent(self);
            const affinity = offer.quote.global.affinity;
            const affinityData = affinity ? parent.affinityLookups.data.find((aff) => aff.affinity === affinity) : null;
            const offerState = offer.quote.correctedAddress?.state;

            // In non-prod, agency test users may not have real affinity codes in the lookups table,
            // so treat any user with an affinity code as an agency user
            const isProd = import.meta.env.VITE_STACKERY_ENVIRONMENT === 'production';
            const isAgencyUser = isProd ? Boolean(affinityData?.isAgency) : Boolean(affinity);

            track('Staff Offer Opened', {
              state: offerState,
              isAgency: isAgencyUser,
              offerType: offer.quote.selectedOption,
              offerId: offer.id,
              webUserId: offer.webUserId
            });

            // Update user property for Appcues filtering
            // Appcues can trigger on events but can only filter based on user properties
            if (offerState) {
              const branchSessionUsername = sessionStorage.getItem('branchSessionUsername');
              if (branchSessionUsername) {
                // Extract username from 'staff-{username}' format
                const staffUsername = branchSessionUsername.replace('staff-', '');
                identify(
                  {
                    username: staffUsername,
                    mostRecentOfferState: offerState
                  },
                  false
                );
              }
            }
          } catch (error) {
            console.warn('Error tracking Staff Offer Opened event:', error);
            // Don't block UI if tracking fails
          }

          // A pending auto-redirect wins over the merge modal; dismissRedirect reopens it if the
          // agent keeps this version
          if (!self.showRaterInterstitialModal && self.canMergeOffer && !self.redirectTarget) {
            self.toggleRaterInterstitialModal(true);
          }
        } else {
          self.errors = response.errors;
          self.clearPreviousOfferOnFailedLoad(offerId);
        }
      } catch (error) {
        if (isSuperseded()) {
          return;
        }
        self.clearPreviousOfferOnFailedLoad(offerId);
        self.handleError(error);
      } finally {
        // A superseded load leaves these to the load that replaced it
        if (!isSuperseded()) {
          self.loading = false;
          // Reset in finally so a failed load doesn't leave the flag stuck true and suppress the
          // newer-offer check on the next navigation
          self.setNavigatingInternally(false);
        }
      }
    }),
    // A failed load must not leave the previous offer on screen. With no offer, the provider
    // shows the error and sends the user back to search, as it does when a first load fails.
    clearPreviousOfferOnFailedLoad(offerId) {
      if (self.offer?.id !== offerId) {
        self.offer = null;
      }
    },
    getOfferVendorData: flow(function* getOfferVendorData(offerId) {
      const { client } = getEnv(self);
      try {
        const response = yield client.query({
          query: GET_OFFER_VENDOR_DATA,
          variables: { offerId },
          fetchPolicy: 'no-cache'
        });
        // Guard against stale responses when user navigated to a different offer
        if (self.offer?.id === offerId) {
          self.vendorData = response.data?.offer?.vendorData ?? null;
        }
      } catch (error) {
        const isAuthError = error?.graphQLErrors?.some(
          (e) => e?.errorType === 'Unauthorized' || e?.errorType === 'Forbidden'
        );
        if (!isAuthError) {
          console.error('[getOfferVendorData] Unexpected error fetching vendor data:', error);
        }
        if (self.offer?.id === offerId) {
          self.vendorData = null;
        }
      }
    }),
    fetchAffinityData: flow(function* fetchAffinityData() {
      const affinity = self.offer?.quote?.global?.affinity;

      if (affinity) {
        const { affinityLookups } = getParent(self);
        yield affinityLookups.getByAffinity(affinity);
      }
    }),
    handleError(error) {
      const parsed = formattedError(error);
      if (parsed) {
        self.loading = false;
        const errors = [...self.errors, { ...parsed, timestamp: new Date().toISOString() }];
        self.errors = errors;
        self.triggerOfferDialog(true);
      }
      return parsed;
    },
    toggleRaterInterstitialModal(toggle) {
      self.showRaterInterstitialModal = toggle;
    },
    checkForNewerStaffEditedOffer: flow(function* checkForNewerStaffEditedOffer(currentOffer) {
      const { client } = getEnv(self);
      const newerOffer = yield findNewerStaffEditedOffer(client, currentOffer, self.userPreferences);
      // Skip if another offer was opened meanwhile: don't redirect away from it. requestedOfferId
      // catches one still loading (self.offer is the old offer until it lands); self.offer catches
      // a re-rate, which changes the offer id without calling getOffer.
      if (newerOffer && currentOffer.id === self.offer?.id && currentOffer.id === self.requestedOfferId) {
        self.redirectTarget = newerOffer;
      }
    }),
    dismissRedirect() {
      self.redirectTarget = null;
      if (!self.showRaterInterstitialModal && self.canMergeOffer) {
        self.toggleRaterInterstitialModal(true);
      }
    },
    setNavigatingInternally(isInternal) {
      self.isNavigatingInternally = isInternal;
    },
    addDriversAddCarsAndRecalculateCluster: flow(function* addDriversAddCarsAndRecalculateCluster({
      newDrivers,
      newVins,
      newTrailerVins,
      revisedQuoteDetails,
      offerId,
      history
    }) {
      const { client } = getEnv(self);
      self.loading = true;
      self.recalculatingPrice = true;

      try {
        const response = yield withRetry(
          () =>
            client.mutate({
              mutation: ADD_DRIVERS_CARS_AND_RECALCULATE,
              variables: {
                offerId,
                newDrivers,
                newVins,
                newTrailerVins,
                revisedQuoteDetails
              }
            }),
          'addDriversAddCarsAndRecalculateCluster',
          MUTATION_MAX_ATTEMPTS
        );

        if (response.data) {
          self.errors = [];
          const { cluster } = response.data;
          const offer =
            cluster.offers.find(({ code }) => code === 'C') || cluster.offers.find(({ code }) => code === 'S'); // try C or fallback to S
          if (hasRejection(offer)) {
            // Adding a driver or a car can hard-reject a line, which drops it out of offer.options
            // while self.selectedOption still names it. redirectOnRejection repairs the selection
            // the same way recalculateQuoteToCluster and fillOutSkippedDataPulls already do.
            self.redirectOnRejection(offer, history);
          } else {
            self.offer = applyCosmeticDamageBusinessLogic(offer);
            self.setDrivers(offer.quote.drivers);
            self.setNotifications();
            self.setVINDiscrepancies(newVins);
            self.setFormAction({ dirty: false, stale: self.isStale, fromHomeVerification: false });
            history.push(`/offer/${offer.id}`);
          }
        } else {
          self.errors = response.errors;
        }
      } catch (error) {
        self.handleError(error);
      } finally {
        self.loading = false;
        self.recalculatingPrice = false;
        // Reset record recheck
      }
    }),
    recalculateQuoteToCluster: flow(function* recalculateQuoteToCluster(
      offerId,
      details,
      history,
      pushToCheckout = false
    ) {
      const { client } = getEnv(self);
      // #35629: a silent refresh of a stale prebind payment schedule continues to checkout on success.
      // Assume it failed until the re-rate proves otherwise, so every early exit (error, rejection)
      // leaves the footer on the explicit Update prompt.
      const continueToCheckout = self.continueToCheckoutAfterRecalculate;
      self.continueToCheckoutAfterRecalculate = false;
      if (continueToCheckout) {
        self.paymentScheduleRefreshFailed = true;
      }
      self.loading = true;
      self.recalculatingPrice = true;
      try {
        const response = yield withRetry(
          () =>
            client.query({
              query: RECALCULATE_QUOTE_TO_CLUSTER,
              variables: {
                offerId,
                revisedQuoteDetails: mapRevisedQuoteDetails(details)
              },
              fetchPolicy: 'no-cache'
            }),
          'recalculateQuoteToCluster'
        );

        if (response.data) {
          self.errors = [];
          const { cluster } = response.data;
          const offer =
            cluster.offers.find(({ code }) => code === 'C') || cluster.offers.find(({ code }) => code === 'S'); // try C or fallback to S
          yield self.checkForPriorQuoteWithPreBindUWRejections();

          if (hasRejection(offer)) {
            if (pushToCheckout) {
              history.push(`/offer/${offer.id}/${details.selectedOption}/checkout`);
            } else {
              self.redirectOnRejection(offer, history);
            }
          } else {
            self.offer = applyCosmeticDamageBusinessLogic(offer);
            if (self.formAction === FormAction.PreBindUWValidation) {
              self.addToPreBindUWRejections(offer);
            }
            self.setDrivers(offer.quote.drivers);
            self.setFormAction({ dirty: false, stale: self.isStale, fromHomeVerification: false });
            self.setNeedsPreBindUWForm();
            self.setNotifications();

            // #35629: the refresh is only done when the re-rated option's schedule now starts today.
            const refreshedScheduleIsCurrent = continueToCheckout && !self.stalePaymentScheduleDate;
            if (refreshedScheduleIsCurrent) {
              self.paymentScheduleRefreshFailed = false;
            }
            if (pushToCheckout || refreshedScheduleIsCurrent) {
              history.push(`/offer/${offer.id}/${details.selectedOption}/checkout`);
            } else {
              history.push(`/offer/${offer.id}`);
            }
          }
        } else {
          self.errors = response.errors;
        }
      } catch (error) {
        self.handleError(error);
      } finally {
        self.loading = false;
        self.recalculatingPrice = false;
      }
    }),
    uwApproveForCheckout: flow(function* uwApproveForCheckout(offerId, uwApprovalDetails, history) {
      const { client } = getEnv(self);
      self.loading = true;
      try {
        // Call UW approval endpoint
        const approvalResponse = yield withRetry(
          () =>
            client.mutate({
              mutation: UW_APPROVE_FOR_CHECKOUT,
              variables: {
                offerId,
                uwApprovalDetails
              }
            }),
          'uwApproveForCheckout',
          MUTATION_MAX_ATTEMPTS
        );

        if (approvalResponse.data) {
          self.errors = [];
          const { cluster } = approvalResponse.data;
          const approvedOffer =
            cluster.offers.find(({ code }) => code === 'C') || cluster.offers.find(({ code }) => code === 'S');

          yield self.checkForPriorQuoteWithPreBindUWRejections();

          self.offer = applyCosmeticDamageBusinessLogic(approvedOffer);
          if (self.formAction === FormAction.PreBindUWValidation) {
            self.addToPreBindUWRejections(approvedOffer);
          }
          self.setDrivers(approvedOffer.quote.drivers);
          self.setFormAction({ dirty: false, stale: self.isStale, fromHomeVerification: false });
          self.setNeedsPreBindUWForm();
          self.setNotifications();
          history.push(`/offer/${approvedOffer.id}`);
        } else {
          self.errors = approvalResponse.errors;
        }
      } catch (error) {
        self.handleError(error);
      } finally {
        self.loading = false;
      }
    }),
    swapHomeRentersOptions: flow(function* swapHomeRentersOptions(history, selectedOption) {
      const { client } = getEnv(self);
      self.loading = true;

      const isRenters = self.offer.options.some((o) => o.type === policyType.ARBundle || o.type === policyType.Renters);

      try {
        const response = yield withRetry(
          () =>
            client.query({
              query: SWAP_HOME_RENTERS,
              variables: {
                offerId: self.offer.id,
                revisedQuoteDetails: { isHomeOwner: !!isRenters, includeRenters: !isRenters, selectedOption }
              },
              fetchPolicy: 'no-cache'
            }),
          'swapHomeRenters'
        );

        if (response.data) {
          self.errors = [];
          const { offer } = response.data;
          if (offer) {
            const bundle = offer.options.find(
              (o) => o.type === policyType.HABundle || o.type === policyType.CABundle || o.type === policyType.ARBundle
            )?.type;
            self.setSelectedOption(bundle);
            self.setCurrentTab(OfferPageTabs.PEOPLE);
            self.setFormAction({ dirty: false, stale: self.isStale, fromHomeVerification: false });
            self.offer = applyCosmeticDamageBusinessLogic(offer);
            history.push(`/offer/${offer.id}`);
          }
        } else {
          self.errors = response.errors;
        }
      } catch (error) {
        self.handleError(error);
      } finally {
        self.loading = false;
      }
    }),
    fillOutSkippedDataPulls: flow(function* fillOutSkippedDataPulls(history, dataPulls) {
      const { client } = getEnv(self);
      const oldOfferId = self.offer.id;
      self.loading = true;

      try {
        const response = yield withRetry(
          () =>
            client.query({
              query: FILL_OUT_SKIPPED_DATA_PULLS,
              variables: {
                offerId: oldOfferId,
                invokedFromStaff: true,
                dataPulls
              },
              fetchPolicy: 'no-cache'
            }),
          'fillOutSkippedDataPulls'
        );

        if (response.data) {
          const { cluster } = response.data;
          const newOffer =
            cluster.offers.find(({ code }) => code === 'C') || cluster.offers.find(({ code }) => code === 'S');
          if (newOffer) {
            let offerDifference = null;
            try {
              offerDifference = yield self.getOfferDifference(oldOfferId, newOffer.id);
            } catch (e) {
              console.log('Error getting offer difference', e);
            }
            if (hasRejection(newOffer)) {
              self.rejectOffer(newOffer);
              self.repairSelectedOption(newOffer);
            } else {
              self.errors = [];
            }
            self.setCurrentTab(OfferPageTabs.PEOPLE);
            self.offer = applyCosmeticDamageBusinessLogic(newOffer);
            // Only once self.offer is the new offer: offerDifference mounts the Report Results modal,
            // which fires its one Staff - Report Results Shown event from its first render. Set
            // earlier, that render saw the pre-report offer, so the event's offerId was the old one
            // and every after-reports premium equalled the before value.
            self.offerDifference = offerDifference;
            self.setFormAction({ dirty: false, stale: self.isStale, fromHomeVerification: false });
            history.push(`/offer/${newOffer.id}`);
          }
        } else {
          self.errors = response.errors;
        }
      } catch (error) {
        self.handleError(error);
      } finally {
        self.loading = false;
      }
    }),
    rejectOffer(offer) {
      const { rejections } = offer;
      self.loading = false;
      self.errors = rejections.map(({ type, value, data }) => ({
        type,
        code: value,
        data: data && JSON.parse(data)
      }));

      self.offer = applyCosmeticDamageBusinessLogic(offer);
      if (self.formAction === FormAction.PreBindUWValidation) {
        self.addToPreBindUWRejections(offer);
      }
      self.setNeedsPreBindUWForm();
      self.setNotifications();
    },
    // A rejection can drop the selected line from offer.options; re-derive the selection the
    // same way the initial load does (getInitialSelectedOption knows the HB/AB-to-HA backing).
    repairSelectedOption(offer) {
      self.setSelectedOption(getInitialSelectedOption(offer));
    },
    setCurrentTab(newTab) {
      self.currentTab = newTab;
    },
    triggerOfferDialog(open = true) {
      self.openDialog = open;
    },
    clearErrors() {
      self.errors = [];
    },
    // { id, accountId } of the policy already bound from this offer, or null (#31622).
    // `forOfferId` discards late-arriving results from a previously opened offer — the
    // guard compares against the latest REQUESTED offer id (not self.offer.id, which
    // still holds the prior offer while the new one is loading and would discard the
    // legitimate result whenever the lookup wins the race against GET_OFFER).
    setBoundPolicy(boundPolicy, forOfferId) {
      if (!shouldApplyBoundPolicyResult(forOfferId, self.requestedOfferId)) {
        return; // late result from a previously opened offer — discard (#31622)
      }
      self.boundPolicy = boundPolicy;
      // #35936: EXACT when the policy was bound from the very offer being viewed; otherwise the
      // policysummary match is for a different/unknown quote and is only a soft warning.
      self.boundPolicyIsExact = Boolean(boundPolicy) && boundPolicy.offerId === forOfferId;
    },
    setSelectedOption(option) {
      self.selectedOption = option;
    },
    bindRequest: flow(function* bindRequest(bindRequestInput) {
      const { client } = getEnv(self);
      self.errors = [];
      // Only set when this attempt fails with response errors; never show an earlier attempt's
      self.rawBindErrorMessages = null;
      self.status = OfferStatus.Purchasing;
      // #35249: remember the attempt so a matching-active-policy warning can be resubmitted with
      // the confirm flag, and clear any warning from a previous attempt.
      self.pendingBindRequestInput = bindRequestInput;
      self.matchingActivePolicyIds.clear();
      const cleanedInput = mapBindRequestToInput(bindRequestInput);
      self.triggerOfferDialog(true);

      try {
        const response = yield client.query({
          query: REQUEST_BIND,
          variables: { request: cleanedInput }
        });
        if (response.data) {
          const { id, systemId } = response.data.requestBind;
          return { id, systemId };
        }

        if (response.errors) {
          self.rawBindErrorMessages = response.errors?.map(({ message }) => message).join(',');
          self.handleError(response.errors);
        }
      } catch (error) {
        const parsedError = self.handleError(error);

        // #35249: a matching active policy is a confirmable warning, not a failure. Keep the dialog
        // open, remember the matching ids, and let the agent choose to purchase anyway.
        if (typeof parsedError.message === 'string' && parsedError.message.includes(MatchingActivePolicyErrorCode)) {
          self.status = OfferStatus.MatchingActivePolicyWarning;
          self.matchingActivePolicyIds = parseMatchingActivePolicyIds(parsedError.message);
          self.triggerOfferDialog(true);
          return { id: undefined, systemId: undefined };
        }

        self.status = MVRErrors[parsedError.code] || PreBindErrors[parsedError.code] || OfferStatus.PurchaseFailed;

        if (DuplicatedBindError === parsedError.code && self.status === OfferStatus.PurchaseFailed) {
          self.status = OfferStatus.DuplicatedBindError;
        }

        if (self.status === OfferStatus.PreBindRejectionsBlocking) {
          parsedError.data.rejections.forEach(({ value }) => {
            self.errors.push({ code: value });
          });
        }
        return { id: undefined, systemId: undefined };
      }
      self.checkoutFormData = null;
      self.status = OfferStatus.Initial;
      self.triggerOfferDialog(false);
    }),
    // #35249: agent confirmed the matching-active-policy warning — resubmit the same bind with the
    // acknowledgement flag so the pre-bind step lets it through. Returns { id, systemId } like
    // bindRequest so the caller can navigate to the purchased screen.
    confirmMatchingActivePolicyBind: flow(function* confirmMatchingActivePolicyBind() {
      const input = self.pendingBindRequestInput;
      if (!input) {
        return { id: undefined, systemId: undefined };
      }
      const retryInput = {
        ...input,
        bindDetails: { ...(input.bindDetails || {}), ignoreMatchingActivePolicyForBind: true }
      };
      return yield self.bindRequest(retryInput);
    }),
    cancelMatchingActivePolicyWarning() {
      self.matchingActivePolicyIds.clear();
      self.pendingBindRequestInput = null;
      self.status = OfferStatus.Initial;
      self.triggerOfferDialog(false);
    },
    downloadQuote: flow(function* downloadQuote({ bypassCache, docContext }) {
      const { client } = getEnv(self);
      self.errors = [];
      self.status = OfferStatus.DownloadingQuote;

      try {
        // The draft doesn't use the phone (the resolver only reads email + mailingAddress),
        // so a bad stored number must never block PDF generation. Two ways it can be bad:
        // the formatter can throw on unparseable input, AND — the #35220 case — it can parse
        // a syntactically valid E.164 whose exchange still violates the NANP (e.g.
        // +18161234567), which the AWSPhone scalar rejects server-side ("Variable 'number'
        // has an invalid value") before the resolver runs. Validate the formatted number and
        // omit anything AWSPhone wouldn't accept.
        let formattedPhone = null;
        if (self.offer?.quote?.phone) {
          try {
            const candidate = phoneNumberFormatter({
              phoneNumber: self.offer.quote.phone.toString()
            });
            formattedPhone = isValidPhoneNumber(candidate) ? candidate : null;
          } catch {
            formattedPhone = null;
          }
        }

        const selectedOption = getQuoteFriendlySelectedOption(self.selectedOption);
        const option = self.offer.options.find((o) => o.type === selectedOption);
        if (!option) {
          // globalThis.Error: the MST `Error` model above shadows the global constructor
          throw new globalThis.Error(
            'The selected option is no longer on this offer. It may have been requoted in another tab. Refresh the page and try again.'
          );
        }

        const quotePDFInput = {
          offerId: self.offer.id,
          optionId: option.id,
          bypassCache,
          docContext: docContext || undefined,
          bindDetails: {
            email: self.offer.quote.email,
            phoneNumbers: formattedPhone ? [{ number: formattedPhone }] : [],
            mailingAddress: self.offer.quote.correctedAddress || undefined
          }
        };

        const response = yield client.query({
          query: DOWNLOAD_QUOTE,
          variables: { details: cleanInput(quotePDFInput) }
        });
        if (!response.data?.quotePdf) {
          throw new globalThis.Error('The application PDF could not be generated. Try again in a moment.');
        }
        window.open(response.data.quotePdf, '_blank');
      } catch (error) {
        const isAuthError =
          error?.graphQLErrors?.some((e) => e?.errorType === 'Unauthorized' || e?.errorType === 'Forbidden') ||
          error?.message?.includes('Not Authorized');
        self.handleError(
          isAuthError
            ? new globalThis.Error('Your role does not have access to generate application documents.')
            : error
        );
      } finally {
        self.status = OfferStatus.Initial;
      }
    }),
    downloadOffer: flow(function* downloadOffer({ offerId, clusterId, policyType, bypassCache, revisedQuoteDetails }) {
      // Farm, and standalone Auto on a brand whose auto docs live in Zywave (CFM), render the Quote
      // PDF through the DocGen pipeline rather than a DocRaptor screenshot of the customer /review
      // page — which the mutual deploys cannot serve (#35921). Deciding here covers every entry
      // point (Documents & sharing card, footer sharing modal, auto premium breakdown).
      if (usesDocGenQuotePdf(policyType)) {
        yield self.downloadQuote({ bypassCache, docContext: 'quote' });
        return;
      }

      const { protocol } = window.location;
      const { client } = getEnv(self);

      self.errors = [];
      self.status = OfferStatus.DownloadingOffer;

      const getClusterId = async (offerId, revisedQuoteDetails) => {
        const response = await client.query({
          query: RECALCULATE_QUOTE_TO_CLUSTER,
          variables: {
            offerId,
            revisedQuoteDetails
          },
          fetchPolicy: 'no-cache'
        });
        if (response?.data) {
          return response.data.cluster.id;
        }
      };

      let fetchedClusterId;
      try {
        fetchedClusterId = yield getClusterId(offerId, revisedQuoteDetails);
      } catch {
        fetchedClusterId = null;
      }

      // have to give public url for docraptor to work properly so replace with dev environment url if localhost
      const offerStoreIsProduction = isProductionEnv(awsExports.stackeryEnvironmentName);
      const host = offerStoreIsProduction
        ? `${awsExports.brandDomain}.com`
        : `${awsExports.stackeryEnvironmentName}.ourbranch.dev`;

      try {
        const response = yield client.query({
          query: DOWNLOAD_OFFER,
          variables: {
            documentUrl: `${protocol}//${host}/review?cid=${
              clusterId || fetchedClusterId
            }&policyType=${policyType}&internal=true&printableVersion=true`,
            documentId: `offer_pdf_${offerId}_${policyType}`,
            bypassCache
          }
        });
        if (!response.data?.offerPdf) {
          throw new globalThis.Error('The Quote PDF could not be generated. Try again in a moment.');
        }
        window.open(response.data.offerPdf, '_blank');
      } catch (error) {
        self.handleError(error);
      }

      self.status = OfferStatus.Initial;
    }),
    // "Simple Quote PDF": the brand-themed react-pdf offer document (farm Z/Y for
    // Forreston/CFM, CFM auto). Uses the getDocraptorDocument resolver with a DISTINCT
    // `simple_quote_pdf_{offerId}_{optionType}` documentId (not the legacy `offer_pdf_`
    // one used by the "Quote PDF" button) so the backend can tell the two apart and the
    // existing button stays on its legacy path (gated by SIMPLE_QUOTE_PDF_ENABLED). #35157.
    downloadSimpleQuotePdf: flow(function* downloadSimpleQuotePdf({ offerId, optionType, clusterId, bypassCache }) {
      const { protocol } = window.location;
      const { client } = getEnv(self);
      self.errors = [];
      self.status = OfferStatus.DownloadingSimpleQuote;

      const offerStoreIsProduction = isProductionEnv(awsExports.stackeryEnvironmentName);
      const host = offerStoreIsProduction
        ? `${awsExports.brandDomain}.com`
        : `${awsExports.stackeryEnvironmentName}.ourbranch.dev`;

      try {
        const response = yield client.query({
          query: DOWNLOAD_OFFER,
          variables: {
            // documentUrl is only used by the legacy DocRaptor fallback; the react-pdf
            // path keys off documentId and ignores it.
            documentUrl: `${protocol}//${host}/review?cid=${clusterId}&policyType=${optionType}&internal=true&printableVersion=true`,
            documentId: `simple_quote_pdf_${offerId}_${optionType}`,
            bypassCache
          }
        });
        if (!response.data?.offerPdf) {
          throw new globalThis.Error('The Simple Quote PDF could not be generated. Try again in a moment.');
        }
        window.open(response.data.offerPdf, '_blank');
      } catch (error) {
        self.handleError(error);
      } finally {
        self.status = OfferStatus.Initial;
      }
    }),
    addReferral: flow(function* addReferral({ quoterEmail, ...referralDetails }) {
      const { client } = getEnv(self);
      self.addingReferral = true;
      try {
        if (referralDetails.id) {
          // sometimes the customerId comes in as id
          // (worth figuring out how)
          referralDetails.customerId ||= referralDetails.id;
          delete referralDetails.id;
        }
        const response = yield client.mutate({
          mutation: ADD_REFERRAL,
          variables: { referralDetails: cleanInput(referralDetails) }
        });
        if (response.data.friendBuyLink) {
          Cookies.set(
            'offerData',
            {
              offerId: self.offer.id,
              option: self.selectedOption,
              email: quoterEmail
            },
            { expires: 24 } // expires within a day
          );
          window.open(response.data.friendBuyLink, '_self');
        } else {
          self.handleError({ message: 'Failed to generate referral link' });
        }
      } catch (error) {
        self.handleError(error);
      } finally {
        self.addingReferral = false;
      }
    }),
    sendPurchaseLink: flow(function* sendPurchaseLink(webUserId, offerId, purchaseInformation) {
      const { client } = getEnv(self);
      self.loading = true;
      self.failedToSendLink = false;
      self.errors = [];
      try {
        const response = yield client.mutate({
          mutation: SEND_PURCHASE_LINK,
          variables: { webUserId, offerId, purchaseInformation }
        });
        if (response.data.offer) {
          const { offer } = response.data;
          if (offer) {
            self.offer = applyCosmeticDamageBusinessLogic(offer);
          }
        } else {
          self.failedToSendLink = true;
        }
      } catch (error) {
        self.handleError(error);
      } finally {
        self.loading = false;
      }
    }),
    sendOfferLinkToRep: flow(function* sendOfferLinkToRep(offerId) {
      const { client } = getEnv(self);
      const response = yield client.mutate({
        mutation: SEND_OFFER_LINK_TO_REP,
        variables: { args: { offerId } }
      });
      return response.data?.sendOfferLinkToRep;
    }),
    pollFetchPolicyFromOffer: flow(function* pollFetchPolicyFromOffer(offerId) {
      // waiting on brtoa to finish executing, usually takes around 2s
      const { client } = getEnv(self);
      self.errors = [];
      self.status = OfferStatus.FetchingPolicyFromOffer;

      try {
        const { data } = yield pollPolicyTable(client, offerId, {
          maxRetries: FETCH_POLICY_ATTEMPTS
        });
        if (data?.accountId) {
          self.status = OfferStatus.FetchSuccessful;
          self.purchasedAccountId = data.accountId;
          return data.accountId;
        }
      } catch {
        self.status = OfferStatus.FetchFailed;
      }
    }),
    getDriverAndCarSuggestions: flow(function* getCarAndDriverSuggestions(offerId) {
      try {
        const { client } = getEnv(self);

        const { data } = yield client.query({
          query: GET_CAR_DRIVER_SUGGESTIONS,
          variables: {
            offerId
          }
        });
        // Another offer was opened meanwhile: these belong to the old one
        if (offerId !== self.offer?.id) {
          return;
        }
        if (data?.driverSuggestions || data?.carSuggestions) {
          self.carAndDriverSuggestions = data;
        }
      } catch (error) {
        if (offerId === self.offer?.id) {
          self.handleError(error);
        }
      }
    }),
    setNotifications: function setNotifications() {
      const { affinityLookups } = getParent(self);

      const notifications = [];
      const affinity = self.offer?.quote?.global?.affinity;
      const connectedHomeEnabled = self.offer?.quote?.connectedHome && self.offer?.quote?.connectedHome?.providerName;
      const providerIsAbodeProPlan = self.offer?.quote?.connectedHome?.providerName === ABODE_PRO_PLAN_PROVIDER_NAME;
      const underwritingRulesAreSkipped = self.offer?.skipUnderwritingUntil;

      // abode pro plan
      if (affinity === ABODE_PRO_PLAN_AFFINITY && connectedHomeEnabled && providerIsAbodeProPlan) {
        notifications.push(Notification.Offer.AbodeProPlan);
      }

      // connected home partner, but connected home not on
      if (
        affinity &&
        affinityLookups.data.find((aff) => aff.affinity === affinity)?.data?.homeSecurity &&
        !connectedHomeEnabled
      ) {
        notifications.push(Notification.Offer.ConnectedHome);
      }

      // signing up for home security
      const newConnectedHomeSignUpCustomer =
        self.offer?.quote?.global?.homeSecurityPartnerCustomerType === NEW_CUSTOMER_HOME_SECURITY.SIGN_UP_TYPE;
      if (connectedHomeEnabled && newConnectedHomeSignUpCustomer) {
        notifications.push(Notification.Offer.NewConnectedHomeSignUpCustomer);
      }

      const UWNotifications = getUWNotification({
        offer: self.offer,
        priorQuoteWithPreBindUWRejections: self.priorQuoteWithPreBindUWRejections,
        needsPreBindUWForm: self.needsPreBindUWForm
      });

      if (UWNotifications.length) {
        notifications.push(...UWNotifications);
      }

      // skip underwriting notification
      if (underwritingRulesAreSkipped) {
        try {
          const skipUntilDate = new Date(underwritingRulesAreSkipped);

          if (!isNaN(skipUntilDate.getTime())) {
            // Get the policy's timezone for proper comparison
            const policyState = self.offer?.quote?.correctedAddress?.state;
            const timezone = (policyState && stateToTzMap[policyState]) || 'America/New_York';

            // Compare YYYY-MM-DD strings in the policy timezone. The previous
            // approach (utcToZonedTime + isSameDay on both Dates) had the
            // same midnight-shift bug that broke isStale (#30635). String
            // compare here is equivalent to "same day OR future day".
            const skipUntilDayStr = formatInTimeZone(skipUntilDate, timezone, 'yyyy-MM-dd');
            const todayStr = formatInTimeZone(new Date(), timezone, 'yyyy-MM-dd');
            const isStillActive = skipUntilDayStr >= todayStr;

            if (isStillActive) {
              notifications.push(Notification.Offer.SkipUnderwritingActive);
            }
          }
        } catch (error) {
          // Invalid date format or timezone error, skip notification
          console.log('Error processing skipUnderwritingUntil date:', underwritingRulesAreSkipped, error);
        }
      }

      if (self.offer?.rejections?.some((r) => r.type === 'home')) {
        notifications.push(Notification.Offer.HomeRejection);
      }

      if (
        self.offer?.rejections?.some((rejection) => {
          return (
            rejectionTypeToPolicyTypeMap[rejection.type]?.some((type) => type === self.selectedOption) &&
            rejection.level === 'soft'
          );
        })
      ) {
        notifications.push(Notification.Offer.CannotPurchase);
      }

      // if someone is interested in an auto policy in a state that is included in lapsedLicenseMonthsStates lookup.
      if (
        self.selectedOption &&
        self.selectedOption.includes(policyType.Auto) &&
        lapsedLicenseMonthsStates[self.state]
      ) {
        notifications.push(Notification.Offer.LapsedDriversLicenses);
      }

      // roof year notification
      if (
        self.selectedOption &&
        self.selectedOption.includes(policyType.Home) &&
        !self.offer.quote.home.roofYearChangedManually &&
        new Date().getFullYear() - self.offer.quote.home.roofYear >= 15
      ) {
        notifications.push(Notification.Offer.OfferWithOlderRoof);
      }

      // potential drivers identified
      if (
        self.offer?.quote?.removedDrivers?.length &&
        self.offer?.quote?.removedDrivers.some((d) => !d.removedReason)
      ) {
        notifications.push(Notification.Offer.PotentialDriversIdentified);
      }
      if (notifications.length) {
        self.notifications = notifications;
      } else {
        self.notifications = null;
      }
    },
    setNeedsPreBindUWForm: function setNeedsPreBindUWForm() {
      self.needsPreBindUWForm = determineIfOfferNeedsUWForm({
        offer: self.offer,
        priorQuoteWithPreBindUWRejections: self.priorQuoteWithPreBindUWRejections,
        setFormManually: false,
        selectedOption: self.selectedOption
      });
    },
    setVINDiscrepancies: function setVINDiscrepancies(newVins) {
      let vinDiscrepancies = [];

      let carsAfterUpdatingOffer = [];
      carsAfterUpdatingOffer = self?.offer?.quote?.cars;

      // filter the newVins array to remove successfully added vins
      vinDiscrepancies = newVins.filter((newCar) => {
        let i;
        let vinSuccessful = false;
        // loop through carsAfterUpdatingOffer array to see if each newVin object's VIN exists
        // in carsAfterUpdatingOffer
        // if the VIN does exist, do not add that car to vinDiscrepancies
        for (i = 0; i < carsAfterUpdatingOffer.length; i += 1) {
          if (newCar.VIN === carsAfterUpdatingOffer[i].VIN) {
            vinSuccessful = true;
          }
        }
        return !vinSuccessful;
      });

      // if vinDiscrepancies contains objects, map to the store
      if (vinDiscrepancies.length) {
        const discrepancyError = {
          code: 5009
        };

        const errors = [...self.errors, discrepancyError];

        self.errors = errors;
        self.vinDiscrepancies = vinDiscrepancies;
        self.triggerOfferDialog(true);
      } else {
        self.vinDiscrepancies = null;
      }
    },

    clearVinDiscrepancies: function clearVinDiscrepancies() {
      self.vinDiscrepancies = [];
    },

    recheckDrivingRecord() {
      self.triggerDrivingRecordRecheck = true;
    },

    saveCheckoutFormData(values) {
      if (values?.id && isEqual(values?.id, self.checkoutFormData?.id)) {
        self.checkoutFormData = { ...self.checkoutFormData, ...values };
      } else {
        self.checkoutFormData = { ...values };
      }
    },
    setShowConnectedHomeModal(open) {
      self.showConectedHomeModal = open;
    },
    setShowFarmAssociationModal(open) {
      self.showFarmAssociationModal = open;
    },
    addToPreBindUWRejections: flow(function* addToPreBindUWRejections(offer) {
      const { client } = getEnv(self);
      const ineligibleForHome =
        get(offer, 'quote.preBindUWCheck.homeVerification.ineligibleForHomeDueToUWSelfReport') ||
        self.priorQuoteWithPreBindUWRejections.ineligibleForHome ||
        false;
      const ineligibleForAuto =
        get(offer, 'quote.preBindUWCheck.autoVerification.ineligibleForAutoDueToUWSelfReport') ||
        self.priorQuoteWithPreBindUWRejections.ineligibleForAuto ||
        false;
      if ((ineligibleForHome || ineligibleForAuto) && isProductionEnv(awsExports.stackeryEnvironmentName)) {
        try {
          const response = yield withRetry(
            () =>
              client.mutate({
                mutation: ADD_TO_PRE_BIND_UW_REJECTIONS,
                variables: {
                  webUserId: offer.webUserId,
                  ineligibleForHome,
                  ineligibleForAuto,
                  attestationAgent: get(offer, 'quote.preBindUWCheck.attestationAgent')
                }
              }),
            'addToPreBindUWRejections',
            MUTATION_MAX_ATTEMPTS
          );
          // rejectOffer doesn't wait for this: another customer's offer may be open by now
          if (offer.webUserId !== self.offer?.webUserId) {
            return;
          }
          if (response.data) {
            self.setPriorQuoteWithPreBindUWRejectionsData(response.data?.addToPreBindUWRejections);
          }
        } catch (error) {
          if (offer.webUserId === self.offer?.webUserId) {
            self.handleError(error);
          }
        }
      }
    }),
    removeFromPreBindUWRejections: flow(
      function* removeFromPreBindUWRejections(homeOverride, autoOverride, teamLeader) {
        const ineligibleForHome = !homeOverride ? self.priorQuoteWithPreBindUWRejections?.ineligibleForHome : false;
        const ineligibleForAuto = !autoOverride ? self.priorQuoteWithPreBindUWRejections?.ineligibleForAuto : false;

        const { client } = getEnv(self);
        // Captured once so every retry targets this customer, even if another offer opens meanwhile
        const { webUserId } = self.offer;
        try {
          const response = yield withRetry(
            () =>
              client.mutate({
                mutation: ADD_TO_PRE_BIND_UW_REJECTIONS,
                variables: {
                  webUserId,
                  ineligibleForHome,
                  ineligibleForAuto,
                  attestationAgent: teamLeader
                }
              }),
            'removeFromPreBindUWRejections',
            MUTATION_MAX_ATTEMPTS
          );
          if (webUserId !== self.offer?.webUserId) {
            return;
          }
          if (response.data) {
            self.setPriorQuoteWithPreBindUWRejectionsData(response.data?.addToPreBindUWRejections);
          }
        } catch (error) {
          if (webUserId === self.offer?.webUserId) {
            self.handleError(error);
          }
        }
      }
    ),
    clearRejectCodes: flow(function* clearRejectCodes() {
      const { client } = getEnv(self);
      self.loading = true;
      try {
        const response = yield client.query({
          query: CLEAR_REJECT_CODES,
          variables: { offerId: self.offer.id, invokedFromStaff: true }
        });
        if (response.data) {
          self.offer = applyCosmeticDamageBusinessLogic(response.data.offer);
          self.errors = [];
          self.areRejectCodesCleared = true;
        } else {
          self.errors = response.errors;
        }
      } catch (error) {
        self.handleError(error);
      } finally {
        self.loading = false;
      }
    }),
    updateOfferAdvancedConfiguration: flow(function* updateOfferAdvancedConfiguration(skipUnderwritingUntil) {
      const { client } = getEnv(self);
      try {
        const response = yield client.mutate({
          mutation: UPDATE_OFFER_ADVANCED_CONFIGURATION,
          variables: { offerId: self.offer.id, skipUnderwritingUntil }
        });
        if (response.data?.updateOfferAdvancedConfiguration?.success) {
          // Update the local offer data by creating a new offer object
          self.offer = {
            ...self.offer,
            skipUnderwritingUntil: skipUnderwritingUntil
          };
          self.setNotifications();
          return { success: true, message: response.data.updateOfferAdvancedConfiguration.message };
        }

        const errorMessage = response.data?.updateOfferAdvancedConfiguration?.message || 'Unknown error occurred';
        return { success: false, message: errorMessage };
      } catch (error) {
        self.handleError(error);
        return { success: false, message: error.message };
      }
    }),
    getIsLicensedForState(session) {
      /*
      FormFields have this logic already built in via the permissions prop
      Therefore, only use this function if wanting to use this logic for items that are NOT form fields
      */
      const state = self.offer?.quote.correctedAddress.state;
      return session.allowedStates.includes(state) && !session.viewOnly;
    },
    setPriorQuoteWithPreBindUWRejectionsData(data) {
      if (data === null) {
        self.priorQuoteWithPreBindUWRejections = {
          ineligibleForHome: false,
          ineligibleForAuto: false
        };
      } else {
        self.priorQuoteWithPreBindUWRejections = {
          ineligibleForHome: data.ineligibleForHome,
          ineligibleForAuto: data.ineligibleForAuto
        };
      }
      // Both depend on these flags. The UW form decision first: the notifications read it.
      // getOffer waits for this lookup before it can fail on member policies, so the new offer
      // never keeps the previous offer's UW form decision.
      self.setNeedsPreBindUWForm();
      self.setNotifications();
    },
    checkForPriorQuoteWithPreBindUWRejections: flow(function* checkForPriorQuoteWithPreBindUWRejections() {
      const { client } = getEnv(self);
      const webUserId = self.offer?.webUserId;
      // Another customer's offer was opened meanwhile: these flags are not theirs
      const isForAnotherCustomer = () => self.offer?.webUserId !== webUserId;
      try {
        const response = yield withRetry(
          () =>
            client.query({
              query: GET_PRE_BIND_UW_REJECTIONS_BY_WEB_USER_ID,
              variables: {
                webUserId
              }
            }),
          'getPreBindUWRejectionsByWebUserId'
        );
        if (isForAnotherCustomer()) {
          return;
        }
        self.setPriorQuoteWithPreBindUWRejectionsData(
          response?.data?.getPreBindUWRejectionsByWebUserId ? response?.data?.getPreBindUWRejectionsByWebUserId : null
        );
      } catch (error) {
        if (!isForAnotherCustomer()) {
          self.handleError(error);
        }
      } finally {
        self.setNotifications();
      }
    }),
    setShowUDRModal(open) {
      self.showUDRModal = open;
    },
    setShowBranchPlusMultiPolicyDiscountModal(open = true) {
      self.showBranchPlusMultiPolicyDiscountModal = open;
    },
    setIncludeBranchPlusMultiPolicyDiscount(include) {
      self.includeBranchPlusMultiPolicyDiscount = include;
    },
    redirectOnRejection(offer, history) {
      self.rejectOffer(offer);
      self.repairSelectedOption(offer);
      self.setCurrentTab(OfferPageTabs.PEOPLE);
      history.push(`/offer/${offer.id}`);
    },
    setScrollTo(scrollTo) {
      self.scrollTo = scrollTo;
    },
    fetchExistingCustomerPolicies: flow(function* fetchExistingCustomerPolicies(existingCustomerId) {
      const { client } = getEnv(self);

      const { data } = yield client.query({
        query: GET_EXISTING_CUSTOMER_POLICIES,
        variables: {
          id: existingCustomerId
        },
        fetchPolicy: 'no-cache'
      });
      // The offer now open is linked to someone else (or no one): these are not its member's
      if (self.offer?.quote?.existingCustomer?.id !== existingCustomerId) {
        return;
      }

      const {
        account: { policies }
      } = data || {};

      self.existingCustomerPolicies = policies;
    }),
    setOfferMarketTier() {
      // Cleared first so an offer that can't be rated (this throws) never shows the last offer's tier
      self.marketTierRating = null;
      // A failed load clears the offer, and the provider's reaction calls this on that change too
      if (!self.offer) {
        return;
      }
      // If no options on offer means it's a rejected offer
      if (!self.offer.options.length || isFarmBasedPolicyType(self.selectedOption)) {
        self.marketTierRating = 0;
        return;
      }

      // If home bundle or home with multiline discount, get market tier rating based on existing active auto policy
      const multilineDiscountType = self.offer.quote.forcedMultiPolicyDiscount;
      const linkedToExistingCustomer = Boolean(self.offer.quote.existingCustomer?.id);
      let selectedOption = self.selectedOption;

      if (linkedToExistingCustomer && selectedOption === policyType.Home && multilineDiscountType === policyType.Auto) {
        // If home with auto monoline, treat like home bundle
        selectedOption = policyType.HomeBundle;
      }

      if (linkedToExistingCustomer && selectedOption === policyType.Auto && multilineDiscountType === policyType.Home) {
        // If auto with home monoline, treat like auto bundle
        selectedOption = policyType.AutoBundle;
      }

      // If more than one active auto policy, use the oldest one
      const autoPolicy =
        selectedOption === policyType.HomeBundle
          ? self.existingCustomerPolicies
              .filter((policy) => policy.policyType === policyType.Auto)
              .reduce(
                (oldest, policy) => (!oldest || policy.effectiveDate < oldest.effectiveDate ? policy : oldest),
                null
              )
          : null;

      let details = {};
      if (autoPolicy) {
        const autoPolicyDetails = policyDetailsFromSegments(autoPolicy);

        details = {
          policyType: selectedOption,
          state: autoPolicy.state,
          drivers: autoPolicyDetails.drivers,
          cars: autoPolicyDetails.cars,
          priorInsuranceTerm: autoPolicyDetails.global.continuousAutoCoverageStartDate,
          biLimit: autoPolicyDetails.autoCoverage.policyLimitBIPD,
          rateControlDate: autoPolicyDetails.global.rateControlDate
        };
      } else {
        // Otherwise take details from the offer
        details = {
          policyType: selectedOption,
          state: self.state,
          drivers: self.offer.quote.drivers,
          cars: self.offer.quote.cars,
          priorInsuranceTerm: self.offer.quote.global.continuousAutoCoverageStartDate,
          biLimit: self.offer.quote.autoCoverage.policyLimitBIPD,
          rateControlDate: self.offer.quote.global.rateControlDate
        };
      }

      self.marketTierRating = calculateMarketTier(details);
    },
    setUiHelpers(uiHelpers) {
      self.uiHelpers = { ...self.uiHelpers, ...uiHelpers };
    },
    clearUiHelpers() {
      self.uiHelpers = initialState.uiHelpers;
    },
    getMergedOffer: flow(function* getMergedOffer(offerId, rater) {
      const { client } = getEnv(self);
      try {
        const response = yield client.query({
          query: GET_MERGED_OFFER,
          fetchPolicy: 'no-cache',
          variables: { offerId, rater }
        });
        if (response.data) {
          self.errors = [];
          const { offer } = response.data;
          if (offer) {
            return {
              id: offer.id,
              usingNonVerifiedData: offer.usingNonVerifiedData
            };
          }
        } else {
          return null;
        }
      } catch (error) {
        self.handleError(error);
      }
    }),
    clearOfferDifference: function clearOfferDifference() {
      self.offerDifference = null;
    },
    getOfferDifference: flow(function* getOfferDifference(oldOfferId, newOfferId) {
      const { client } = getEnv(self);
      try {
        const response = yield client.query({
          query: GET_OFFER_DIFFERENCE,
          fetchPolicy: 'no-cache',
          variables: { oldOfferId, newOfferId }
        });
        if (response.data) {
          self.errors = [];
          const { getOfferDifference } = response.data;
          // Returned rather than stored: the caller sets offerDifference once the new offer is in place.
          return getOfferDifference.success ? getOfferDifference.difference : null;
        }
        return null;
      } catch (error) {
        console.log(`Error getting offer difference: ${error}`);
        return null;
      }
    }),
    // Document management actions
    uploadDocument: flow(function* uploadDocument(file, _onProgress, options = {}) {
      if (!self.offer.webUserId) {
        throw new globalThis.Error('No webUserId available for document upload');
      }

      self.documentsLoading = true;

      try {
        // Support both supporting documents and task documents
        // options can include: { taskName, offerId }. The offerId defaults to this offer's: the API
        // needs it for agents on migrated farm offers, which it can't find by webUserId (#36196).
        const uploadedDocument = yield uploadOfferDocument(self.offer.webUserId, file, {
          offerId: self.offer.id,
          ...options
        });

        // Add the document to the store
        self.documents.push(uploadedDocument);
        self.documentCount = self.documents.length;

        return uploadedDocument;
      } catch (error) {
        self.handleError(error);
        throw error;
      } finally {
        self.documentsLoading = false;
      }
    }),

    fetchDocuments: flow(function* fetchDocuments() {
      const { id: offerId, webUserId } = self.offer;
      if (!webUserId) {
        return;
      }
      // Another offer was opened meanwhile: these documents are the old one's
      const isForAnotherOffer = () => self.offer?.id !== offerId;

      self.documentsLoading = true;

      try {
        const documentsData = yield fetchOfferDocuments(webUserId, offerId);
        if (isForAnotherOffer()) {
          return;
        }

        // Clear existing documents and add fetched ones
        self.documents.clear();

        // Combine internal and regular documents
        const allDocuments = [...(documentsData.internal || []), ...(documentsData.regular || [])];

        // Transform documents to match OfferDocument model
        const transformedDocuments = allDocuments.map((doc) => ({
          id: doc.path,
          fileName: doc.path.split('/').pop() || 'Unknown',
          uploadedAt: doc.lastModified,
          displayName: doc.displayName ?? null
        }));

        transformedDocuments.forEach((doc) => {
          self.documents.push(doc);
        });

        self.documentCount = self.documents.length;

        return transformedDocuments;
      } catch (error) {
        if (isForAnotherOffer()) {
          return;
        }
        self.handleError(error);
        throw error;
      } finally {
        self.documentsLoading = false;
      }
    }),

    downloadDocument: flow(function* downloadDocument(path) {
      try {
        yield downloadOfferDocument(path, self.offer.id);
      } catch (error) {
        self.handleError(error);
        throw error;
      }
    }),

    // Short-lived signed URL for an <img> preview of an offer document.
    getDocumentSignedUrl(path) {
      return getOfferDocumentSignedUrl(path, self.offer.id);
    },

    renameDocument: flow(function* renameDocument(key, label) {
      if (!self.offer.webUserId) {
        throw new globalThis.Error('No webUserId available for document rename');
      }
      yield setOfferDocumentLabel({ webUserId: self.offer.webUserId, offerId: self.offer.id, key, label });
      yield self.fetchDocuments();
    }),

    deleteDocument: flow(function* deleteDocument(path) {
      if (!self.offer.webUserId) {
        throw new globalThis.Error('No webUserId available for document delete');
      }
      yield deleteOfferDocument(self.offer.webUserId, path);
      yield self.fetchDocuments();
    }),

    sendOfferForUnderwritingApproval: flow(
      // #35936: `acknowledgeExistingActivePolicy` is set on the retry after the agent confirms the
      // fuzzy "member already has an active policy" warning (the exact-offer-id case stays a hard
      // block and is never reachable here because the footer disables the button for it).
      function* sendOfferForUnderwritingApproval(
        offerId,
        sentToUnderwritingAt,
        sentToUnderwritingBy,
        history,
        { acknowledgeExistingActivePolicy = false } = {}
      ) {
        const { client } = getEnv(self);
        try {
          self.loading = true;
          const response = yield client.mutate({
            mutation: SEND_OFFER_FOR_UNDERWRITING_APPROVAL,
            variables: {
              offerId,
              sentToUnderwritingAt,
              sentToUnderwritingBy,
              acknowledgeExistingActivePolicy
            }
          });

          const cluster = response.data?.sendOfferForUnderwritingApproval;
          if (!cluster || !cluster.offers || cluster.offers.length === 0) {
            return {
              ok: false,
              error: {
                message: 'Failed to send offer for underwriting approval'
              }
            };
          }

          // Get the offer from cluster (prefer 'C' code, fallback to 'S')
          const newOffer =
            cluster.offers.find(({ code }) => code === 'C') ||
            cluster.offers.find(({ code }) => code === 'S') ||
            cluster.offers[0];

          // Update the offer in the store with the recalculated offer from the rater
          self.offer = newOffer;

          // If the offer ID changed, navigate to the new offer
          if (history && newOffer.id !== offerId) {
            history.push(`/offer/${newOffer.id}`);
          }
          return {
            ok: true
          };
        } catch (error) {
          // #35936: a fuzzy active-policy match is a confirmable warning, not a failure (mirrors the
          // checkout dup check, #35249). Surface it so the footer can show a "send anyway?" dialog
          // and retry with the ack flag — without popping the generic error modal handleError would.
          const message =
            error?.message || (error?.graphQLErrors || []).map((graphQLError) => graphQLError.message).join(' ') || '';
          if (message.includes(MatchingActivePolicyErrorCode)) {
            return { ok: false, needsActivePolicyAck: true, activePolicyIds: parseMatchingActivePolicyIds(message) };
          }
          self.handleError(error);
          return { ok: false };
        } finally {
          self.loading = false;
        }
      }
    )
  }));

export default OfferStore;
